import React from 'react';
import { Tooltip } from 'antd';
import { CheckCircleFilled, ExclamationCircleFilled, SyncOutlined } from '@ant-design/icons';
import moment from 'moment';

interface ParsedFieldIndicatorProps {
	fieldName: string;
	currentValue: any;
	parsedValue: any;
	show?: boolean;
}

const NUMERIC_EPSILON = 0.001; // Treat numbers within this as equal (floating point)

const ParsedFieldIndicator: React.FC<ParsedFieldIndicatorProps> = ({
	fieldName,
	currentValue,
	parsedValue,
	show = true,
}) => {
	if (!show || parsedValue === undefined || parsedValue === null) {
		return null;
	}

	const normalizeValue = (value: any) => {
		if (value === null || value === undefined) return '';
		if (typeof value === 'number') return String(value);
		if (typeof value === 'string') return value.trim();
		if (moment.isMoment(value)) return value.format('YYYY-MM-DD');
		if (value instanceof Date) return moment(value).format('YYYY-MM-DD');
		return String(value);
	};

	const toNumber = (value: any): number | null => {
		if (value === null || value === undefined) return null;
		if (typeof value === 'number' && !Number.isNaN(value)) return value;
		const n = parseFloat(value);
		return Number.isNaN(n) ? null : n;
	};

	const valuesMatch = (current: any, parsed: any): boolean => {
		const numCurrent = toNumber(current);
		const numParsed = toNumber(parsed);
		if (numCurrent !== null && numParsed !== null) {
			return Math.abs(numCurrent - numParsed) < NUMERIC_EPSILON;
		}
		return normalizeValue(current) === normalizeValue(parsed);
	};

	const isObject = (val: any) =>
		val !== null && typeof val === 'object' && !moment.isMoment(val) && !(val instanceof Date);

	let isMatched = false;

	if (isObject(currentValue) && isObject(parsedValue)) {
		const parsedKeys = Object.keys(parsedValue).filter(
			(key) => !key.startsWith('_') && key !== 'key' && key !== 'id'
		);

		isMatched = parsedKeys.every((key) => {
			const currentVal = currentValue[key];
			const parsedVal = parsedValue[key];

			if (
				(currentVal === undefined || currentVal === null) &&
				(parsedVal === undefined || parsedVal === null)
			) {
				return true;
			}

			return valuesMatch(currentVal, parsedVal);
		});
	} else {
		isMatched = valuesMatch(currentValue, parsedValue);
	}

	if (isMatched) {
		return (
			<Tooltip title="Matched with invoice file">
				<CheckCircleFilled
					style={{
						color: '#52c41a',
						fontSize: '16px',
						marginLeft: '8px',
						verticalAlign: 'middle',
					}}
				/>
			</Tooltip>
		);
	}
	if (fieldName === 'invoiceTotalAfterTax') {
		return (
			<Tooltip title="The invoice total from the PDF doesn’t match the total after tax here. Please review">
				<ExclamationCircleFilled
					style={{
						color: '#FF4D4F',
						fontSize: '16px',
						marginLeft: '8px',
						verticalAlign: 'middle',
					}}
				/>
			</Tooltip>
		);
	}

	return (
		<Tooltip title="This field was manually changed from the parsed PDF value">
			<div
				className="ml-2 h-4 w-4 bg-cover bg-center"
				style={{
					WebkitMaskImage: `url('/invoice_changed_item.svg')`,
					WebkitMaskRepeat: 'no-repeat',
					WebkitMaskSize: 'contain',
					WebkitMaskPosition: 'center',
					maskImage: `url('/invoice_changed_item.svg')`,
					maskRepeat: 'no-repeat',
					maskSize: 'contain',
					maskPosition: 'center',
					backgroundColor: 'rgba(0, 0, 0, 0.45)',
				}}
			/>
		</Tooltip>
	);
};

export default ParsedFieldIndicator;
